import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Per-page selection state. Stores the rich payload per selected party
// (not just an id) so the selection bar can render counts (phones,
// addresses, suggested-rows-in-selection) without a re-query.
//
// Optional sessionStorage persistence via `storageKey`: pass something
// like `aoi:<id>` or `pool_order:<id>` and selections survive a click
// into a party detail page + browser back. sessionStorage (not local)
// because the lifetime is "this tab's planning session" — closing the
// tab is an explicit reset.

export interface SelectablePartyRow {
  party_id: string;
  display_name: string;
  /** From pool-order Suggested tab; null on AOI roster + Matched tab. */
  is_suggested?: boolean;
  has_phone: boolean;
  has_address: boolean;
  /** Name as it appears on the upstream source — used by Copy-names. */
  source_label: string;
}

export interface RosterSelection {
  count: number;
  withPhone: number;
  withAddress: number;
  suggested: number;
  isSelected: (id: string) => boolean;
  toggle: (row: SelectablePartyRow) => void;
  selectMany: (rows: SelectablePartyRow[]) => void;
  clear: () => void;
  selectedRows: SelectablePartyRow[];
}

const STORAGE_PREFIX = 'mi.roster-selection.';

function hydrateFromStorage(storageKey: string | undefined): Map<string, SelectablePartyRow> {
  if (!storageKey || typeof window === 'undefined') return new Map();
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + storageKey);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw) as SelectablePartyRow[];
    if (!Array.isArray(parsed)) return new Map();
    return new Map(parsed.map(r => [r.party_id, r]));
  } catch {
    return new Map();
  }
}

export function useRosterSelection(storageKey?: string): RosterSelection {
  const [byId, setById] = useState<Map<string, SelectablePartyRow>>(
    () => hydrateFromStorage(storageKey)
  );

  // Re-hydrate when storageKey changes (e.g. operator navigates between
  // two AOIs without unmounting the component tree). Skip on first
  // render — initial state already covers that case.
  const firstRunRef = useRef(true);
  useEffect(() => {
    if (firstRunRef.current) { firstRunRef.current = false; return; }
    setById(hydrateFromStorage(storageKey));
  }, [storageKey]);

  // Persist on every change. Empty selection clears the key to keep
  // sessionStorage tidy.
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      if (byId.size === 0) window.sessionStorage.removeItem(STORAGE_PREFIX + storageKey);
      else                 window.sessionStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify([...byId.values()]));
    } catch { /* quota exceeded / private mode — non-fatal */ }
  }, [byId, storageKey]);

  const toggle = useCallback((row: SelectablePartyRow) => {
    setById(prev => {
      const next = new Map(prev);
      if (next.has(row.party_id)) next.delete(row.party_id);
      else next.set(row.party_id, row);
      return next;
    });
  }, []);

  const selectMany = useCallback((rows: SelectablePartyRow[]) => {
    setById(prev => {
      const next = new Map(prev);
      for (const r of rows) next.set(r.party_id, r);
      return next;
    });
  }, []);

  const clear = useCallback(() => setById(new Map()), []);

  const summary = useMemo(() => {
    let withPhone = 0, withAddress = 0, suggested = 0;
    for (const r of byId.values()) {
      if (r.has_phone) withPhone++;
      if (r.has_address) withAddress++;
      if (r.is_suggested) suggested++;
    }
    return { count: byId.size, withPhone, withAddress, suggested };
  }, [byId]);

  const isSelected = useCallback((id: string) => byId.has(id), [byId]);
  const selectedRows = useMemo(() => [...byId.values()], [byId]);

  return { ...summary, isSelected, toggle, selectMany, clear, selectedRows };
}

// Format names for TruePeopleSearch paste. Strips trailing " AND" so
// the spouse-truncated entries from pool orders don't carry it; leaves
// everything else verbatim. One name per line, no other characters.
export function formatNamesForTruePeopleSearch(rows: SelectablePartyRow[]): string {
  return rows
    .map(r => (r.source_label ?? r.display_name).trim())
    .map(s => s.replace(/\s+AND\s*$/i, '').trim())
    .filter(Boolean)
    .join('\n');
}
